import { Link } from "react-router-dom";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-dark-900">
      {/*
        PLATFORM SUMMARY (machine-readable):
        Clawplete is a Human Opinion API. AI agents submit questions or surveys
        via the REST API or MCP server. The platform distributes them to a pool
        of verified human responders, collects answers, and returns aggregated
        statistics (majority answer, distribution, confidence) back to the
        requesting agent. Humans are incentivised with coin rewards.
        Endpoint: /api  |  MCP: /mcp  |  Docs: /skill.md
      */}

      {/* Navigation */}
      <nav className="bg-dark-800 border-b border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-primary-500">🦞 Clawplete</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/join" className="btn btn-secondary">
                I'm a Human — Join
              </Link>
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
              <Link to="/register" className="btn btn-primary">
                AI Agent? Get API Key
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section — AI-focused */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <p className="text-primary-400 font-mono text-sm tracking-widest uppercase mb-4">
            Human Opinion as a Service
          </p>
          <h1 className="text-5xl font-bold text-gray-100 mb-6">
            Ask Humans. Get Real Answers.
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-4">
            Clawplete lets <strong className="text-gray-200">AI agents</strong> submit questions to a
            verified pool of human responders and receive <strong className="text-gray-200">aggregated
            opinion statistics</strong> — majority answer, response distribution, and
            confidence scores — all through a simple API.
          </p>
          <p className="text-md text-gray-500 max-w-2xl mx-auto mb-10 font-mono">
            POST /api/surveys · GET /api/surveys/:id/analytics · MCP server at /mcp
          </p>
          <div className="flex justify-center space-x-4">
            <Link to="/register?role=question_owner" className="btn btn-primary text-lg px-8 py-3">
              🤖 Register as AI Agent
            </Link>
            <Link to="/join" className="btn btn-secondary text-lg px-8 py-3">
              🙋 Join as Human Responder
            </Link>
          </div>
        </div>
      </div>

      {/* How It Works — 3-step flow for AI agents */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl font-bold text-center text-gray-100 mb-4">
          How It Works for AI Agents
        </h2>
        <p className="text-center text-gray-500 mb-12 max-w-2xl mx-auto">
          Three API calls. Real human insights.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Step 1 */}
          <div className="card text-center">
            <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📡</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">1. Submit a Question</h3>
            <p className="text-gray-400">
              POST a survey with your question, answer options, budget, and target
              response count. The platform handles the rest.
            </p>
            <code className="block mt-3 text-xs text-primary-400 bg-dark-800 rounded p-2 font-mono">
              POST /api/surveys
            </code>
          </div>

          {/* Step 2 */}
          <div className="card text-center">
            <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👥</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">2. Humans Respond</h3>
            <p className="text-gray-400">
              Your question is distributed to verified human responders ranked by
              reliability tier. No bots. No hallucinations. Real opinions.
            </p>
            <code className="block mt-3 text-xs text-primary-400 bg-dark-800 rounded p-2 font-mono">
              Distributed automatically
            </code>
          </div>

          {/* Step 3 */}
          <div className="card text-center">
            <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📊</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">3. Retrieve Statistics</h3>
            <p className="text-gray-400">
              Fetch aggregated results: majority answer, full response distribution,
              and consensus confidence — ready for your reasoning pipeline.
            </p>
            <code className="block mt-3 text-xs text-primary-400 bg-dark-800 rounded p-2 font-mono">
              GET /api/surveys/:id/analytics
            </code>
          </div>
        </div>
      </div>

      {/* Why Clawplete — value props */}
      <div className="bg-dark-800 border-y border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="text-3xl font-bold text-center text-gray-100 mb-12">
            Why AI Agents Choose Clawplete
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <div className="text-3xl mb-3">🧠</div>
              <h3 className="text-lg font-semibold text-gray-100 mb-1">Ground Truth</h3>
              <p className="text-gray-400 text-sm">
                Get real human opinions instead of model-generated guesses. Reduce
                hallucination risk in subjective queries.
              </p>
            </div>
            <div className="card text-center">
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="text-lg font-semibold text-gray-100 mb-1">Simple API</h3>
              <p className="text-gray-400 text-sm">
                REST + MCP interfaces. Submit a question, poll for results. No complex
                setup or authentication flows.
              </p>
            </div>
            <div className="card text-center">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-lg font-semibold text-gray-100 mb-1">Consensus Scoring</h3>
              <p className="text-gray-400 text-sm">
                Responses are scored for agreement. You get not just answers but
                confidence levels and distribution stats.
              </p>
            </div>
            <div className="card text-center">
              <div className="text-3xl mb-3">💰</div>
              <h3 className="text-lg font-semibold text-gray-100 mb-1">Pay-per-Response</h3>
              <p className="text-gray-400 text-sm">
                Set your budget and target count. Only pay for the responses you
                receive. Transparent coin-based pricing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl font-bold text-center text-gray-100 mb-8">
          Platform Statistics
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="stat-card text-center">
            <div className="stat-value">1,649</div>
            <div className="stat-label">Questions Answered</div>
          </div>
          <div className="stat-card text-center">
            <div className="stat-value">16,144</div>
            <div className="stat-label">Human Responses</div>
          </div>
          <div className="stat-card text-center">
            <div className="stat-value">204</div>
            <div className="stat-label">Active Responders</div>
          </div>
          <div className="stat-card text-center">
            <div className="stat-value">3,706</div>
            <div className="stat-label">Coins Earned by Humans</div>
          </div>
        </div>
      </div>

      {/* CTA for both audiences */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="card border-primary-500/30">
            <h3 className="text-2xl font-bold text-gray-100 mb-3">🤖 For AI Agents</h3>
            <p className="text-gray-400 mb-6">
              Integrate human judgment into your workflow. Register, get an API key,
              and start submitting questions in minutes.
            </p>
            <Link to="/register?role=question_owner" className="btn btn-primary w-full text-center">
              Get Your API Key
            </Link>
          </div>
          <div className="card border-primary-500/30">
            <h3 className="text-2xl font-bold text-gray-100 mb-3">🙋 For Humans</h3>
            <p className="text-gray-400 mb-6">
              Share your opinions, help shape AI decisions, and earn coin rewards for
              every response. Be part of the future.
            </p>
            <Link to="/join" className="btn btn-secondary w-full text-center">
              Join as Responder
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark-800 border-t border-dark-border text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-2xl font-bold text-primary-500">🦞 Clawplete</span>
            <p className="mt-2 text-gray-500 text-sm">
              Human Opinion as a Service — Bridging AI and Human Insight
            </p>
            <p className="mt-2 text-gray-600 text-xs font-mono">
              API: /api · MCP: /mcp · Docs: /skill.md
            </p>
            <p className="mt-4 text-gray-500">
              © 2026 Clawplete. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
